import {
  Button,
  Col,
  Descriptions,
  Dropdown,
  Row,
  Segmented,
  Space,
  Tag,
} from 'antd';
import { groupBy, includes, map } from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import { AddButton, Export, PersonDone } from '../../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../../common/CommonUseLazyQuery';
import {
  ALLOWED_ACTION_KEYS,
  ALLOWED_ACTION_TYPE,
  BREAKPOINTS,
  GA_EVENT,
  GA_LABEL,
  PROJECT_ROLES_KEYS,
  REGISTER_LOG_STATUS,
  REGISTER_STATUS_CLASSNAME,
  REGISTER_STATUS_LABEL,
  REGISTER_VIEW_TYPE,
  ROUTES,
  SESSION_STORAGE_KEYS,
  TAB_KEYS,
  USER_ROLES,
} from '../../../../../../../common/constants';
import { getValidToken } from '../../../../../../../common/tokenManager';
import { Event } from '../../../../../../../common/trackEvents';
import useRouter from '../../../../../../../common/useRouter';
import {
  removeHistoryStateData,
  titleCase,
  transformAggregatesForExportURL,
} from '../../../../../../../common/utils';
import CanPerform from '../../../../../../../components/CanPerform';
import CollapsibleFilterWrapper from '../../../../../../../components/CollapsibleFilterWrapper';
import CommonPopover from '../../../../../../../components/CommonPopover';
import CommonSelect from '../../../../../../../components/CommonSelect';
import SearchComponent from '../../../../../../../components/SearchComponent';
import { GET_REGISTER } from '../../../../../graphql/Queries';
import ApproversDrawer from '../../eqcTypes/ApproversDrawer';
import EntriesTable from './EntriesTable';
import SetsTable from './SetsTable';

const getStorageKey = (id, showListView) =>
  `${showListView ? SESSION_STORAGE_KEYS?.REGISTER_AGGREGATES_LOGS_KEY : SESSION_STORAGE_KEYS?.REGISTER_AGGREGATES_ENTRIES_KEY}-${id}`;

const RegisterDetails = ({ registerId, globalTeamFilter = [], isMeMode }) => {
  const {
    navigate,
    params: { projectId },
    location,
  } = useRouter();
  const registerNavFilter = location?.state?.registerNavFilter;
  const {
    state,
    getCurrentUser,
    getCurrentTenant,
    getTenantUser,
    getCurrentUserRole,
  } = useContext(AppContext);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const tenantId = getCurrentTenant() || getTenantUser()?.tenant?.id;
  const currentUser = getCurrentUser();
  const [showListView, setShowListView] = useState(true);
  const [registerDetailsData, setRegisterDetailsData] = useState([]);
  const [isLogsDataEmpty, setIsLogsDataEmpty] = useState(true);
  const [isEntriesDataEmpty, setIsEntriesDataEmpty] = useState(true);
  const [registerDetailsSearchValue, setRegisterDetailsSearchValue] =
    useState('');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState();
  const [showDeletedEntries, setShowDeletedEntries] = useState(false);
  const [open, setOpen] = useState(false);
  const [drawerData, setDrawerData] = useState();
  const [selectedAggregates, setSelectedAggregates] = useState({});
  const [logColumnFilters, setLogColumnFilters] = useState({});
  const [entryColumnFilters, setEntryColumnFilters] = useState({});
  const [logSortedInfo, setLogSortedInfo] = useState({});
  const [entrySortedInfo, setEntrySortedInfo] = useState({});
  const navRegisterFilterColumn = location?.state?.registerLogColumnFilter;
  const navRegisterDetailsSearchValue =
    location?.state?.registerDetailsSearchValue;
  const navRegisterLogSortedInfo = location?.state?.registerLogSortedInfo;
  const isAuditor =
    getCurrentUserRole() === USER_ROLES.EXTERNAL_AUDITOR ||
    state?.projectRole === PROJECT_ROLES_KEYS.AUDITOR;
  const isProjectAdminOrInspector = includes(
    [PROJECT_ROLES_KEYS.PROJECT_ADMIN, PROJECT_ROLES_KEYS.INSPECTOR],
    state?.projectRole,
  );
  const [getRegisterDetails] = useLazyQueryWithCallbacks(GET_REGISTER, {
    variables: { getRegisterId: registerId },
    fetchPolicy: 'cache-and-network',
    onError() {},
    onCompleted(res) {
      setRegisterDetailsData(res?.getRegister);
    },
  });

  useEffect(() => {
    if (registerId) {
      const storageKey = getStorageKey(registerId, showListView);
      try {
        // eslint-disable-next-line no-undef
        const storedValue = sessionStorage?.getItem(storageKey);
        if (storedValue) {
          setSelectedAggregates(JSON?.parse(storedValue));
        } else {
          setSelectedAggregates({});
        }
      } catch {
        setSelectedAggregates({});
      }
    }
  }, [registerId, showListView]);

  useEffect(() => {
    if (registerId) {
      const storageKey = getStorageKey(registerId, showListView);
      try {
        if (Object?.keys(selectedAggregates)?.length > 0) {
          // eslint-disable-next-line no-undef
          sessionStorage?.setItem(
            storageKey,
            JSON?.stringify(selectedAggregates),
          );
        } else {
          // eslint-disable-next-line no-undef
          sessionStorage?.removeItem(storageKey);
        }
      } catch {
        // Silent error handling
      }
    }
  }, [selectedAggregates, registerId, showListView]);

  useEffect(() => {
    if (registerId) {
      getRegisterDetails({ variables: { getRegisterId: registerId } });
    }
  }, [registerId]);

  useEffect(() => {
    if (navRegisterDetailsSearchValue) {
      setRegisterDetailsSearchValue(navRegisterDetailsSearchValue);
    }

    if (navRegisterFilterColumn) {
      setLogColumnFilters(navRegisterFilterColumn);
    }

    if (navRegisterLogSortedInfo) {
      const tableSortedInfo = {
        columnKey: navRegisterLogSortedInfo?.field,
        order: navRegisterLogSortedInfo?.order === 'ASC' ? 'ascend' : 'descend',
      };
      setLogSortedInfo(tableSortedInfo);
    }
  }, [
    location?.state?.registerDetailsSearchValue,
    location?.state?.registerLogColumnFilter,
    location?.state?.registerLogSortedInfo,
  ]);
  // Commented for future use
  // const renderDownloadPdfButton = () => {
  //   const token = getToken();
  //   const url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/report/register/${showListView ? 'logs' : 'entries'}?access_token=Bearer ${token}&tenantId=${tenantId}&registerId=${registerId}`;
  //   return (
  //     <Button
  //       type="ghost"
  //       className="export-button d-flex b-0 justify-center width-percent-100"
  //       target="_blank"
  //       href={!isDataEmpty && url}
  //       onClick={() => {
  //         Event(GA_EVENT.EXPORT_PROJECT_REGISTER_PDF, {
  //           label: GA_LABEL.EXPORT_PROJECT_REGISTER_PDF,
  //           // eslint-disable-next-line no-undef
  //           pathname: window?.location?.href,
  //           project_id: projectId,
  //           project_register_id: registerId,
  //           user_id: currentUser?.id,
  //           user_name: currentUser?.name,
  //           tenant_id: currentUser?.tenantUser?.tenant?.id,
  //           tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
  //         });
  //       }}
  //     >
  //       PDF
  //     </Button>
  //   );
  // };

  const renderDownloadCsvButton = () => {
    return (
      <div
        id="register-export-csv-button"
        className="d-flex"
        onClick={async () => {
          if (showListView ? isLogsDataEmpty : isEntriesDataEmpty) {
            return;
          }
          const token = await getValidToken();
          const aggregatedFields =
            transformAggregatesForExportURL(selectedAggregates);
          let url = `${process.env.REACT_APP_REPORT_SERVER_URL}/api/v1/export-csv/report/register/${showListView ? 'logs' : 'entries'}?access_token=Bearer ${token}&tenantId=${tenantId}&registerId=${registerId}&isMeMode=${isMeMode}`;
          if (registerDetailsSearchValue) {
            url += `&search=${registerDetailsSearchValue}`;
          }
          if (globalTeamFilter?.length > 0) {
            url += `&projectAgencyIds=${globalTeamFilter.join(',')}`;
          }
          if (approvalStatusFilter) {
            url += `&status=${approvalStatusFilter}`;
          }
          if (Object?.keys(aggregatedFields)?.length > 0) {
            const aggregateParamsArray = Object?.entries(
              aggregatedFields,
            )?.map(([operation, fieldIds]) => {
              const idString = fieldIds?.join(',');
              return `${operation?.toLocaleLowerCase()}=${idString}`;
            });
            url += `&${aggregateParamsArray?.join('&')}`;
          }
          if (showDeletedEntries) {
            url += `&showDeletedFields=${showDeletedEntries}`;
          }
          if (showListView && Object?.keys(logColumnFilters)?.length > 0) {
            const updatedColumnFilters = Object?.fromEntries(
              Object?.entries(logColumnFilters)?.filter(
                ([, value]) => value?.length > 0,
              ),
            );
            if (Object?.keys(updatedColumnFilters)?.length > 0) {
              const columnFiltersParam = encodeURIComponent(
                JSON?.stringify(updatedColumnFilters),
              );
              url += `&columnFilters=${columnFiltersParam}`;
            }
          }
          if (!showListView && Object?.keys(entryColumnFilters)?.length > 0) {
            const updatedColumnFilters = Object?.fromEntries(
              Object?.entries(entryColumnFilters)?.filter(
                ([, value]) => value?.length > 0,
              ),
            );
            if (Object?.keys(updatedColumnFilters)?.length > 0) {
              const columnFiltersParam = encodeURIComponent(
                JSON?.stringify(updatedColumnFilters),
              );
              url += `&columnFilters=${columnFiltersParam}`;
            }
          }
          if (showListView && logSortedInfo?.columnKey) {
            url += `&sortBy=${logSortedInfo?.columnKey}&sortOrder=${logSortedInfo?.order === 'ascend' ? 'ASC' : 'DESC'}`;
          }
          if (!showListView && entrySortedInfo?.columnKey) {
            url += `&sortBy=${entrySortedInfo?.columnKey}&sortOrder=${entrySortedInfo?.order === 'ascend' ? 'ASC' : 'DESC'}`;
          }
          Event(GA_EVENT.EXPORT_PROJECT_REGISTER_CSV, {
            label: GA_LABEL.EXPORT_PROJECT_REGISTER_CSV,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            project_register_id: registerId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          // eslint-disable-next-line no-undef
          window.open(url, '_blank');
        }}
      >
        CSV
      </div>
    );
  };

  const onSearchChange = (value) => {
    if (!value) {
      removeHistoryStateData(navigate, location, 'registerDetailsSearchValue');
    }
    setRegisterDetailsSearchValue(value);
  };

  const hasDeletedEntries = useMemo(() => {
    const hasDeletedDetails = registerDetailsData?.registerDetails?.some(
      (detail) => detail?.deletedAt,
    );
    const hasDeletedFields = registerDetailsData?.registerFormFields?.some(
      (field) => field?.deletedAt,
    );
    return hasDeletedDetails || hasDeletedFields;
  }, [registerDetailsData]);

  const getLevelWiseApprovers = (approvers) => {
    const levelWiseApprovers = groupBy(approvers, (b) => {
      return b?.levelNumber;
    });
    return map(Object.keys(levelWiseApprovers), (data) => {
      return (
        <Descriptions.Item
          key={data}
          label={`${levelWiseApprovers?.[data]?.[0]?.levelName}:`}
          span={3}
        >
          {map(levelWiseApprovers?.[data], (approver) => {
            return (
              approver?.externalUserApprover?.name ||
              approver?.internalUserApprover?.name
            );
          }).join(', ')}
        </Descriptions.Item>
      );
    });
  };

  const renderApprovers = () => {
    const approvers = registerDetailsData?.approvers;
    return (
      approvers?.length > 0 && (
        <div className="ml-10">
          {isDesktopViewport ? (
            <CommonPopover
              classNames={{
                root: 'checklist-approvers-popup',
                container: 'checklist-approvers-container',
              }}
              getPopupContainer={() =>
                // eslint-disable-next-line no-undef
                document.querySelector('.register-module')
              }
              title="Approvers info:"
              trigger="hover"
              placement="left"
              destroyOnHidden
              content={
                <div onClick={(e) => e.stopPropagation()}>
                  <Descriptions
                    column={3}
                    layout="vertical"
                    colon={false}
                    className="inner"
                  >
                    {getLevelWiseApprovers(approvers)}
                  </Descriptions>
                </div>
              }
            >
              <PersonDone
                className="checklist-approvers"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              />
            </CommonPopover>
          ) : (
            <PersonDone
              className="checklist-approvers"
              onClick={(e) => {
                e.stopPropagation();
                setDrawerData(approvers);
                setOpen(true);
              }}
            />
          )}
        </div>
      )
    );
  };

  const filterItems = [
    {
      key: 'filter',
      children: (
        <Row
          className="d-flex justify-end filter-search align-center register-details-filter"
          wrap
          gutter={isDesktopViewport ? [10, 10] : [10, 10]}
        >
          {hasDeletedEntries && (
            <Col span={isDesktopViewport ? null : 12}>
              {showDeletedEntries ? (
                <Tag
                  closable
                  onClose={() => {
                    setShowDeletedEntries(false);
                  }}
                  onClick={() => {
                    setShowDeletedEntries(false);
                  }}
                  className="show-all-data-tag"
                >
                  <span>Show Deleted Entries</span>
                </Tag>
              ) : (
                <Button
                  type="default"
                  className="show-all-data-toggle"
                  onClick={() => {
                    setShowDeletedEntries(true);
                  }}
                >
                  Show Deleted Entries
                </Button>
              )}
            </Col>
          )}
          {showListView && (
            <Col span={isDesktopViewport ? null : 12}>
              <CommonSelect
                id="register-approval-status-select"
                placeholder="Approval Status"
                className={`instruction-selector ${
                  isDesktopViewport ? 'width-200' : 'width-percent-100'
                }`}
                value={approvalStatusFilter}
                onChange={(status) => {
                  setApprovalStatusFilter(status);
                  if (!status) {
                    removeHistoryStateData(
                      navigate,
                      location,
                      'approvalStatusFilter',
                    );
                  }
                }}
                allowClear
                options={[
                  ...map(
                    [
                      REGISTER_LOG_STATUS.APPROVAL_PENDING,
                      REGISTER_LOG_STATUS.APPROVED,
                      REGISTER_LOG_STATUS.REDO,
                      REGISTER_LOG_STATUS.DRAFT,
                    ],
                    (status) => {
                      return {
                        key: `approval-status-${status}`,
                        value: status,
                        label: titleCase(status),
                      };
                    },
                  ),
                ]}
              />
            </Col>
          )}

          {isDesktopViewport && (
            <Col>
              <SearchComponent
                id="register-search-component"
                className="search-component width-200"
                getData={onSearchChange}
                defaultValue={
                  registerDetailsSearchValue ||
                  location?.state?.registerDetailsSearchValue
                }
                placeholder={showListView ? 'Search Logs' : 'Search Entries'}
              />
            </Col>
          )}
        </Row>
      ),
    },
  ];

  return (
    <div className="register-module">
      <div className="register-details-header">
        {/* Line 1: Approver Details, Status, Logs/Entries Toggle, Export, Add Button */}
        <div
          className={`${
            !isDesktopViewport ? 'gap-4' : ''
          } width-percent-100 d-flex justify-end align-center mb-10`}
        >
          <div className="d-flex align-center flex-wrap gap-4">
            {renderApprovers()}
            <div className="d-flex align-center">
              <Tag
                className={
                  REGISTER_STATUS_CLASSNAME[registerDetailsData?.status]
                }
              >
                {registerDetailsData?.status}
              </Tag>
            </div>
            <div className={`${isDesktopViewport ? 'mr-10' : 'ml-10 mt-10'}`}>
              <Segmented
                options={[REGISTER_VIEW_TYPE.LOGS, REGISTER_VIEW_TYPE.ENTRIES]}
                value={
                  showListView
                    ? REGISTER_VIEW_TYPE.LOGS
                    : REGISTER_VIEW_TYPE.ENTRIES
                }
                onChange={(e) => {
                  setShowListView(e === REGISTER_VIEW_TYPE.LOGS);
                }}
                id="register-view-toggle"
              />
            </div>
          </div>
          <div
            className={`${
              isDesktopViewport
                ? 'd-flex align-center'
                : 'd-flex align-center mt-10'
            }`}
          >
            <Space className="d-flex">
              <Dropdown
                getPopupContainer={(triggerNode) =>
                  // eslint-disable-next-line no-undef
                  triggerNode?.parentNode || document.body
                }
                menu={{
                  items: [
                    {
                      key: 'csv',
                      label: renderDownloadCsvButton(),
                    },
                  ],
                }}
                trigger={['click']}
                placement="bottom"
              >
                <Button
                  shape="round"
                  type="primary"
                  disabled={showListView ? isLogsDataEmpty : isEntriesDataEmpty}
                  icon={<Export height="26px" />}
                  className="export-button fill-white d-flex b-0"
                  id="register-export-btn"
                >
                  {isDesktopViewport && 'Export'}
                </Button>
              </Dropdown>
            </Space>
            <div className="d-flex align-center">
              <CanPerform
                action={ALLOWED_ACTION_KEYS.ADD_REGISTER_LOG}
                type={ALLOWED_ACTION_TYPE.PROJECT}
              >
                <Button
                  shape="round"
                  icon={<AddButton />}
                  disabled={includes(
                    [
                      REGISTER_STATUS_LABEL.DRAFT,
                      REGISTER_STATUS_LABEL.ARCHIVED,
                      REGISTER_STATUS_LABEL.UNPUBLISHED,
                    ],
                    registerDetailsData?.status,
                  )}
                  type="primary"
                  id="register-add-btn"
                  onClick={() => {
                    navigate(
                      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/add-set`,
                      {
                        state: {
                          registerProperty:
                            registerDetailsData?.registerDetails,
                          registerDetailsData,
                          currentPath: location?.pathname,
                        },
                      },
                    );
                  }}
                >
                  {isDesktopViewport && 'Add'}
                </Button>
              </CanPerform>
              {(!isProjectAdminOrInspector || isAuditor) && (
                <CommonPopover
                  placement="left"
                  content="Only Project Admin or Inspector can create log and add entries in Register"
                >
                  <Tag className="view-only-tag ml-10">View Only</Tag>
                </CommonPopover>
              )}
            </div>
          </div>
        </div>

        {/* Line 2: Date Range Filter, Approval Status Filter, Search - wrapped in CollapsibleFilterWrapper */}
        <CollapsibleFilterWrapper
          id="register-filter-wrapper"
          className="mb-10"
          searchProps={{
            className: 'search-component',
            getData: onSearchChange,
            defaultValue:
              registerDetailsSearchValue ||
              location?.state?.registerDetailsSearchValue,
            placeholder: showListView ? 'Search Logs' : 'Search Entries',
          }}
          items={filterItems}
        />
      </div>
      <div>
        <div className={!showListView && 'd-none'}>
          <SetsTable
            registerDetailsData={registerDetailsData}
            showListView={showListView}
            registerId={registerId}
            setIsDataEmpty={setIsLogsDataEmpty}
            registerNavFilter={registerNavFilter}
            globalTeamFilter={globalTeamFilter}
            searchValue={registerDetailsSearchValue}
            approvalStatus={approvalStatusFilter}
            selectedAggregates={selectedAggregates}
            setSelectedAggregates={setSelectedAggregates}
            showDeletedEntries={showDeletedEntries}
            columnFilters={logColumnFilters}
            setColumnFilters={setLogColumnFilters}
            sortedInfo={logSortedInfo}
            setSortedInfo={setLogSortedInfo}
          />
        </div>
        <div className={showListView && 'd-none'}>
          <EntriesTable
            registerDetailsData={registerDetailsData}
            registerId={registerId}
            showListView={showListView}
            setIsDataEmpty={setIsEntriesDataEmpty}
            globalTeamFilter={globalTeamFilter}
            searchValue={registerDetailsSearchValue}
            selectedAggregates={selectedAggregates}
            setSelectedAggregates={setSelectedAggregates}
            showDeletedEntries={showDeletedEntries}
            columnFilters={entryColumnFilters}
            setColumnFilters={setEntryColumnFilters}
            sortedInfo={entrySortedInfo}
            setSortedInfo={setEntrySortedInfo}
          />
        </div>
      </div>
      {open && !isDesktopViewport && (
        <ApproversDrawer open={open} setOpen={setOpen} height={350}>
          <div onClick={(e) => e.stopPropagation()}>
            <Descriptions
              column={3}
              layout="vertical"
              colon={false}
              className="inner"
            >
              {getLevelWiseApprovers(drawerData)}
            </Descriptions>
          </div>
        </ApproversDrawer>
      )}
    </div>
  );
};
export default RegisterDetails;
